<template>
	<div class="new-page-type-switcher-seo-wrapper">
		<div class="new-page-type-switcher-seo">
			<HiddenHorizontalScrollbar :hideNav="true">
				<ChipButton
					:active="currentPage === pageType"
					v-for="(url, pageType) in urls"
					:key="pageType"
					v-show="urlVisible(pageType)"
				>
					<a v-if="urlVisible(pageType)" :href="url" @click.prevent="toggleTab(pageType)"
						>{{ urlTranslations[pageType] }}
						<FreemiumFilterBadge v-if="isPageAvailableForPro(pageType)" />
					</a>
				</ChipButton>
			</HiddenHorizontalScrollbar>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import ChipButton from '@/ui-components/ChipButton.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import FreemiumFilterBadge from '@/components/filter-bar/FreemiumFilterBadge.vue';
import { isProWebCountry } from '@/helpers/pro-web-helper';
import { CollectionType } from '@/enums/collection-type';
import { NewPageType } from '@/@types/graphql-types';

import { calculateUrl, updateState } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';

defineProps<{
	currentPage: NewPageType;
}>();

const { genresByShortName, subgenresList, providersByShortName, upcomingProviders } = storeToRefs(useConstantStore());
const { filters } = useFilterStore();
const { webLocale } = storeToRefs(useLanguageStore());
const { isPremium } = storeToRefs(useUserStore());
const { activeRoute } = storeToRefs(useRoutingStore());
const { t } = useI18n();
const router = useRouter();

const filtersNew = computed(() => filters(CollectionType.NEW));

const urlTranslations = {
	[NewPageType.New]: t('WEBAPP_NEW_NEWLYADDED'),
	[NewPageType.Upcoming]: t('WEBAPP_CINEMA_UPCOMING'),
	[NewPageType.PriceDrop]: t('WEBAPP_NEW_PRICEDROPS'),
};

const isProCountry = computed(() => isProWebCountry(webLocale.value));

const urls = computed(() => {
	return {
		[NewPageType.New]: calculateUrl(
			router,
			undefined,
			CollectionType.NEW,
			{ ...filtersNew.value, timeline_type: null },
			providersByShortName.value,
			genresByShortName.value,
			subgenresList.value
		),
		[NewPageType.Upcoming]: calculateUrl(
			router,
			undefined,
			CollectionType.UPCOMING,
			{ ...filtersNew.value, timeline_type: null },
			providersByShortName.value,
			genresByShortName.value,
			subgenresList.value
		),
		[NewPageType.PriceDrop]: calculateUrl(
			router,
			undefined,
			CollectionType.PRICEDROPS,
			{ ...filtersNew.value, timeline_type: null },
			providersByShortName.value,
			genresByShortName.value,
			subgenresList.value
		),
	};
});

function isPageAvailableForPro(pageType: NewPageType) {
	return [NewPageType.LeavingSoon].includes(pageType);
}

function urlVisible(pageType: NewPageType) {
	// if url couldn't be calculated, the url/button shouldn't be visible
	if (pageType !== NewPageType.LeavingSoon && urls.value[pageType] === '/') {
		return false;
	}

	if (isPageAvailableForPro(pageType)) {
		return isProCountry.value || isPremium.value;
	}

	if (pageType === NewPageType.Upcoming) {
		const filterProviderIds = filtersNew.value.providers.map((providerShortName: string) => {
			return providersByShortName.value[providerShortName].packageId;
		});
		return filterProviderIds.every((id: number) => upcomingProviders.value.includes(id));
	}

	return true;
}

// we don't save newPageType in a filter, that's why we need to map newPageType to collectionType.
function toggleTab(pageType: NewPageType) {
	const mapper: Partial<Record<NewPageType, CollectionType>> = {
		[NewPageType.New]: CollectionType.NEW,
		[NewPageType.Upcoming]: CollectionType.UPCOMING,
		[NewPageType.PriceDrop]: CollectionType.PRICEDROPS,
	};

	// usually we'd call setFilterValue -> updateRouterState, but newPageType is not a filter value,
	// we need to bypass this by calling StateHelper.updateState directly.
	const newRoute = updateState(
		undefined,
		mapper[pageType]!,
		filtersNew.value,
		providersByShortName.value,
		genresByShortName.value,
		activeRoute.value?.query,
		subgenresList.value
	);

	TrackingHelper.trackEvent('userinteraction', {
		action: 'filter_toggled',
		label: 'new_page_type',
		property: mapper[pageType],
	});

	if (newRoute) router.push(newRoute);
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.new-page-type-switcher-seo-wrapper {
	min-height: 54px;
	background-color: $jw-color-darkblue;
}

.new-page-type-switcher-seo {
	min-height: 54px;
	width: 100%;
	padding: 10px $app-x-margin;
	margin: 0 auto;
	max-width: $app-max-width;

	@media (max-width: $breakpoint-two-rows) {
		padding: 10px 15px;
	}
}
</style>
